import api from './api';

export interface Plan {
  _id: string;
  name: string;
  description: string;
  features: string[];
  price: number;
  duration: number;
  maxProjects: number;
  maxUsers: number;
  status: 'active' | 'inactive';
}

export interface CompanyPlan {
  _id: string;
  company: string;
  plan: Plan | string;
  startDate: Date;
  endDate: Date;
  isActive: boolean;
  paymentStatus: 'pending' | 'paid' | 'failed' | 'cancelled';
  paymentMethod?: string;
  transactionId?: string;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface CompanyPlanListResponse {
  success: boolean;
  count: number;
  pagination: {
    total: number;
    page: number;
    pages: number;
    limit: number;
  };
  data: CompanyPlan[];
}

export interface CompanyPlanResponse {
  success: boolean;
  data: CompanyPlan;
}

export interface CompanyPlanQueryParams {
  page?: number;
  limit?: number;
  active?: boolean;
}

class CompanyPlanService {
  /**
   * Get company plans
   */
  async getCompanyPlans(companyId: string, params: CompanyPlanQueryParams = {}): Promise<CompanyPlanListResponse> {
    try {
      const response = await api.get<CompanyPlanListResponse>(`/companies/${companyId}/plans`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching company plans:', error);
      throw error;
    }
  }

  /**
   * Get company plan by ID
   */
  async getCompanyPlan(companyId: string, planId: string): Promise<CompanyPlan> {
    try {
      const response = await api.get<CompanyPlanResponse>(`/companies/${companyId}/plans/${planId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching company plan:', error);
      throw error;
    }
  }

  /**
   * Get active company plan
   */
  async getActiveCompanyPlan(companyId: string): Promise<CompanyPlan> {
    try {
      const response = await api.get<CompanyPlanResponse>(`/companies/${companyId}/plans/active`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching active company plan:', error);
      throw error;
    }
  }

  /**
   * Create company plan
   */
  async createCompanyPlan(companyId: string, data: Partial<CompanyPlan>): Promise<CompanyPlan> {
    try {
      const response = await api.post<CompanyPlanResponse>(`/companies/${companyId}/plans`, data);
      return response.data.data;
    } catch (error) {
      console.error('Error creating company plan:', error);
      throw error;
    }
  }

  /**
   * Update company plan
   */
  async updateCompanyPlan(companyId: string, planId: string, data: Partial<CompanyPlan>): Promise<CompanyPlan> {
    try {
      const response = await api.put<CompanyPlanResponse>(`/companies/${companyId}/plans/${planId}`, data);
      return response.data.data;
    } catch (error) {
      console.error('Error updating company plan:', error);
      throw error;
    }
  }

  /**
   * Delete company plan
   */
  async deleteCompanyPlan(companyId: string, planId: string): Promise<void> {
    try {
      await api.delete(`/companies/${companyId}/plans/${planId}`);
    } catch (error) {
      console.error('Error deleting company plan:', error);
      throw error;
    }
  }
}

export default new CompanyPlanService();